import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, ShoppingBag, MapPin, Tag, Filter, ChevronRight,
  Car, Home, Smartphone, Shirt, Briefcase, Dog, Dumbbell,
  Package, Heart, Eye, Clock
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const categoryIcons = {
  vehiculos: Car, inmuebles: Home, electronica: Smartphone, hogar: Home,
  ropa: Shirt, servicios: Briefcase, empleo: Briefcase, mascotas: Dog,
  deportes: Dumbbell, otros: Package,
};

const categoryColors = {
  vehiculos: "bg-blue-50 text-blue-700 border-blue-200",
  inmuebles: "bg-amber-50 text-amber-700 border-amber-200",
  electronica: "bg-violet-50 text-violet-700 border-violet-200",
  hogar: "bg-orange-50 text-orange-700 border-orange-200",
  ropa: "bg-pink-50 text-pink-700 border-pink-200",
  servicios: "bg-teal-50 text-teal-700 border-teal-200",
  empleo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mascotas: "bg-yellow-50 text-yellow-700 border-yellow-200",
  deportes: "bg-red-50 text-red-700 border-red-200",
  otros: "bg-gray-50 text-gray-700 border-gray-200",
};

const CATEGORIES = [
  { id: "vehiculos", name: "Vehículos" },
  { id: "inmuebles", name: "Inmuebles" },
  { id: "electronica", name: "Electrónica" },
  { id: "hogar", name: "Hogar" },
  { id: "ropa", name: "Ropa" },
  { id: "servicios", name: "Servicios" },
  { id: "empleo", name: "Empleo" },
  { id: "mascotas", name: "Mascotas" },
  { id: "deportes", name: "Deportes" },
  { id: "otros", name: "Otros" },
];

function formatPrice(price, currency = "HNL") {
  if (!price || price === 0) return "Consultar precio";
  return `L ${price.toLocaleString("es-HN")}`;
}

function ProductCard({ product }) {
  const Icon = categoryIcons[product.category] || Package;
  const colorClass = categoryColors[product.category] || categoryColors.otros;
  const mainPhoto = product.photo_urls?.[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.3 }}
    >
      <Link
        to={`/marketplace/${product.slug}`}
        className="block group h-full"
        data-testid={`product-card-${product.product_id}`}
      >
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100">
          <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
            {mainPhoto ? (
              <img src={getImageUrl(mainPhoto)} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ShoppingBag className="h-12 w-12 text-gray-300" />
              </div>
            )}
            {product.photo_urls?.length > 1 && (
              <span className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                {product.photo_urls.length} fotos
              </span>
            )}
            <div className="absolute top-3 left-3">
              <Badge className={`${colorClass} border text-[10px]`}>
                <Icon className="h-2.5 w-2.5 mr-1" />
                {CATEGORIES.find(c => c.id === product.category)?.name || product.category}
              </Badge>
            </div>
            {product.condition && (
              <div className="absolute bottom-3 left-3">
                <Badge className={`text-[10px] ${product.condition === "nuevo" ? "bg-emerald-500 text-white" : "bg-gray-700 text-white"}`}>
                  {product.condition === "nuevo" ? "Nuevo" : "Usado"}
                </Badge>
              </div>
            )}
          </div>
          <div className="p-4 flex-1 flex flex-col">
            <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1 group-hover:text-primary transition-colors">
              {product.title}
            </h3>
            <p className="text-xl font-bold text-emerald-700 mb-2">
              {formatPrice(product.price, product.currency)}
            </p>
            {product.seller_city && (
              <p className="text-xs text-gray-400 flex items-center gap-1 mb-2">
                <MapPin className="h-3 w-3" /> {product.seller_city}
              </p>
            )}
            <div className="mt-auto pt-3 border-t border-gray-50 flex items-center justify-between">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Eye className="h-3 w-3" /> {product.views || 0}
              </span>
              <span className="text-primary text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Ver <ChevronRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function MarketplacePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 24;

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("skip", String(page * limit));
      params.append("limit", String(limit));
      if (searchQuery) params.append("search", searchQuery);
      if (categoryFilter) params.append("category", categoryFilter);
      if (conditionFilter) params.append("condition", conditionFilter);
      const res = await axios.get(`${API}/public/marketplace?${params}`);
      setProducts(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, categoryFilter, conditionFilter]);

  useEffect(() => {
    fetchProducts();
    axios.post(`${API}/track-page-view?page=marketplace`).catch(() => {});
  }, [fetchProducts]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); };

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gray-950 py-20 md:py-28" data-testid="marketplace-hero">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="absolute top-0 left-1/3 w-[600px] h-[400px] bg-gradient-to-b from-emerald-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[300px] bg-gradient-to-tl from-orange-500/10 to-transparent rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-emerald-400 text-sm mb-6 backdrop-blur-sm">
              <ShoppingBag className="h-4 w-4" />
              Compra y vende en tu comunidad
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Marketplace<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-orange-400">Choluteca</span>
            </h1>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto mb-10">
              Encuentra productos, servicios y oportunidades en la regi&oacute;n sur de Honduras
            </p>
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative flex items-center bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm p-1.5">
                <Search className="absolute left-5 h-5 w-5 text-gray-500" />
                <Input
                  placeholder="Buscar productos, servicios..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0"
                  data-testid="marketplace-search"
                />
                <Button type="submit" className="h-12 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">Buscar</Button>
              </div>
            </form>
            <div className="mt-8">
              <Link to="/marketplace/registro">
                <Button variant="outline" className="rounded-xl border-white/20 text-white hover:bg-white/10" data-testid="sell-cta-btn">
                  <Tag className="h-4 w-4 mr-2" />
                  Publica tu producto gratis
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORY PILLS */}
      <section className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide -mx-4 px-4">
            <button
              onClick={() => { setCategoryFilter(""); setPage(0); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${!categoryFilter ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
              data-testid="cat-all"
            >
              <ShoppingBag className="h-4 w-4" /> Todos
            </button>
            {CATEGORIES.map(cat => {
              const Icon = categoryIcons[cat.id] || Package;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setCategoryFilter(cat.id); setPage(0); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${categoryFilter === cat.id ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                  data-testid={`cat-${cat.id}`}
                >
                  <Icon className="h-4 w-4" /> {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONDITION FILTER */}
      <section className="bg-gray-50/50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4 text-sm">
          <Filter className="h-4 w-4 text-gray-400" />
          {[{ id: "", label: "Todos" }, { id: "nuevo", label: "Nuevo" }, { id: "usado", label: "Usado" }].map(opt => (
            <button
              key={opt.id}
              onClick={() => { setConditionFilter(opt.id); setPage(0); }}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${conditionFilter === opt.id ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-200"}`}
              data-testid={`cond-${opt.id || "all"}`}
            >
              {opt.label}
            </button>
          ))}
          <span className="ml-auto text-gray-400">{total} productos</span>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section className="py-10 bg-gray-50/50" data-testid="marketplace-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse border">
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-4 space-y-2"><div className="h-4 bg-gray-200 rounded" /><div className="h-5 w-24 bg-gray-200 rounded" /></div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <ShoppingBag className="h-16 w-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No hay productos disponibles</h3>
              <p className="text-gray-400 mb-6">{searchQuery || categoryFilter ? "Intenta con otros filtros" : "S\u00e9 el primero en publicar"}</p>
              <Link to="/marketplace/registro">
                <Button className="rounded-xl" data-testid="empty-sell-btn">Publicar ahora</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {products.map(p => <ProductCard key={p.product_id} product={p} />)}
              </div>
              {total > limit && (
                <div className="flex items-center justify-center gap-3 pt-8">
                  <Button variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-xl">Anterior</Button>
                  <span className="text-sm text-gray-500">P&aacute;gina {page + 1} de {Math.ceil(total / limit)}</span>
                  <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total} className="rounded-xl">Siguiente</Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-8 bg-white"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><AdBanner /></div></section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden bg-gray-950">
        <div className="absolute top-0 left-0 w-[400px] h-[300px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <ShoppingBag className="h-12 w-12 text-emerald-400 mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">&iquest;Tienes algo que vender?</h2>
            <p className="text-gray-400 mb-8">Reg&iacute;strate gratis y publica tus productos para que toda la comunidad los vea</p>
            <Link to="/marketplace/registro">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white text-lg px-8 py-6 rounded-2xl" data-testid="cta-register-btn">Crear mi cuenta gratis</Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
